// SeverityBar.jsx
import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";

const fireRiskLevels = [
    'Moderate Fire Risk Zone',
    'High Fire Risk Zone',
    'Very High Fire Risk Zone',
    'Extreme Fire Risk Zone',
    'Maximum Fire Risk Zone'
]

function getActiveSection (value) {

    var value = Math.round(value);

    var activeSection;
    if (value < 21) {
        activeSection = 0;
    } else if (value >= 21 && value < 40) {
        activeSection = 1;
    } else if (value >= 41 && value < 60) {
        activeSection = 2;
    } else if (value >= 61 && value < 80) {
        activeSection = 3;
    } else if (value >= 81 && value <= 100) {
        activeSection = 4;
    }
    return activeSection;
};

function getColors(activeSection) {
    const OPACITY = '80'; // 50% opacity
    const FULL_OPACITY = 'FF'; // 100% opacity
    const COLORS = [
    '#fcd34d', // yellow-300
    '#eab308', // yellow-500
    '#f97316', // orange-500
    '#dc2626', // red-600
    '#7f1d1d', // red-900
    ];

    // Use a map to apply different opacities to each color
    const colors = COLORS.map((color, index) => {
        // Check if the current color's index matches the activeSection
        const opacity = index === activeSection ? FULL_OPACITY : OPACITY;
        return `${color}${opacity}`;
    });

    // Handle the default case for activeSection
    if (activeSection < 0 || activeSection >= COLORS.length) {
        // If activeSection is out of bounds, use the same logic as case 0
        return COLORS.map((color, index) => {
        const opacity = index === 0 ? FULL_OPACITY : OPACITY;
        return `${color}${opacity}`;
        });
    }

    return colors;
}

function getCaptionOpacity(currentSection, activeSection) {
    var opacity = 0.5;
    if (currentSection === activeSection) {
        opacity = 1;
    }
    return opacity;
}

function getCaptionWeight(currentSection, activeSection) {
    var weight = 100;
    if (currentSection === activeSection) {
        weight = 600;
    }
    return weight;
}


export default function FireRankBar({ value = 0, height = 28, showLabels = true }) {

    const activeSection = getActiveSection(value);
    const colors = getColors(activeSection);

  // Normalize input to percent (0 - 100)
  const percent = (() => {
    if (value === null || value === undefined) return 0;
    const n = Number(value);
    if (Number.isNaN(n)) return 0;
    if (n >= 0 && n <= 100) return n; // percent already
    // treat 1..5 as severity
    if (n >= 1 && n <= 5) return ((n - 1) / 4) * 100;
    // clamp otherwise
    return Math.max(0, Math.min(100, n));
  })();

  // Label to show (if value was 1..5 show "Severity X", else show percentage)
  const labelText = (() => {
    //const n = Number(value);
    //if (!Number.isNaN(n) && n >= 1 && n <= 5) return `Severity ${n}`;
    // show integer percent
    //return `${Math.round(percent)}%`;
    return `${percent.toFixed(2)}%`;
  })();

return (
  <Box sx={{ width: "100%", height: "72px", position: "relative", userSelect: "none" }}>


    {/* Optional tick marks under the bar (further down) */}
    <Box
      sx={{
        display: "flex",
        mt: `${Math.max(8, Math.round(height / 4)) + 24}px`,
        gap: 0,
        justifyContent: "space-between",
        px: 0.25,
      }}
    >
      {fireRiskLevels.map((n, index) => (

        <Box key={n} sx={{ flex: 1, textAlign: "center" }}>
          <Typography variant="caption" sx={{ opacity: getCaptionOpacity(index, activeSection), fontWeight: getCaptionWeight(index, activeSection) }}>
            {n}
          </Typography>
        </Box>
      ))}
    </Box>

    {/* The colored bar */}
    <Box
      sx={{
        height: `${height}px`,
        display: "flex",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: 1,
        border: "1px solid rgba(0,0,0,0.00)",
      }}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(percent)}
    >
      {colors.map((c, i) => (
        <Box
          key={i}
          sx={{
            flex: 1,
            bgcolor: c,
            minWidth: 0,
          }}
        />
      ))}
    </Box>

    {/* Pointer & label BELOW the bar */}
    <Box
      sx={{
        position: "absolute",
        left: `${percent}%`,
        top: `calc(${height}px + 23px)`, // position under bar
        transform: "translateX(-50%)",
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        pointerEvents: "none",
      }}
      aria-hidden={!showLabels}
    >
      {/* triangle pointing UP */}
      <Box
        sx={{
          width: 0,
          height: 0,
          borderLeft: "8px solid transparent",
          borderRight: "8px solid transparent",
          borderBottom: "0px solid transparent",
          borderTop: `${Math.max(8, Math.round(height / 4))}px solid rgba(0,0,0,0.85)`,
        }}
      />

      {showLabels && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            fontWeight: 600,
            bgcolor: "transparent",
            whiteSpace: "nowrap",
          }}
        >
          {labelText}
        </Typography>
      )}
    </Box>

  </Box>
);

}

FireRankBar.propTypes = {
  value: PropTypes.number,
  height: PropTypes.number,
  showLabels: PropTypes.bool,
};
